import React, { Dispatch, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../../ui/toast';
import { Button } from '../../../../../ui';
import { CollapseAll, ExpandAll } from '../../../../../ui/icons/icons';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import TopBanner from '../../../../../ui/TopBanner';
import {
  createAutoOutstanding,
  createAutoSuitability,
} from '../../../../../utils/api_service/JobOpenings';
import {
  AutoEvaluation,
  JobDescription,
  showAllTypes,
} from '../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import Collapsible from '../../ui/CollapsibleChildren';
import { RangeSelectInput } from '../../ui/RangeSelectComponent';
import ListFields from './ListFieldComponents';

type JobDescriptionTabProps = {
  autoEvaluation: AutoEvaluation;
  setAutoEvaluation: Dispatch<React.SetStateAction<AutoEvaluation>>;
  evaluationType: 'Auto Suitability' | 'Auto Outstanding';
  importAutoSuitability?: () => void;
  completedElement: () => void;
  editable: boolean;
  jobDescription: JobDescription;
};

export const AutoEvaluationTab = ({
  autoEvaluation,
  setAutoEvaluation,
  evaluationType,
  importAutoSuitability,
  completedElement,
  editable,
  jobDescription,
}: JobDescriptionTabProps) => {
  const { t } = useTranslation(NS.adminPages);
  const AE = ADMIN_PAGES_KEYS.jobOpenings.autoEvaluationTab;
  const rangeSelectOptions: SelectOptions[] = [
    { label: t(AE.greaterThan), value: 'Greater Than' },
    { label: t(AE.lesserThan), value: 'Lesser Than' },
  ];
  const [showAll, setShowAll] = useState<showAllTypes>('showAll');

  const saveAutoEvaluation = async () => {
    try {
      const createAutoEvaluationResponse =
        evaluationType === 'Auto Suitability'
          ? await createAutoSuitability(autoEvaluation)
          : await createAutoOutstanding(autoEvaluation);

      if (createAutoEvaluationResponse.ok) {
        createAutoEvaluationResponse.json().then((data) => {
          setAutoEvaluation(data);
          toast.success(`${evaluationType} Saved Successfully`, {
            position: 'bottom-center',
          });
          completedElement();
        });
      } else {
        toast.error(`${evaluationType} Save Failed`, {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error(`${evaluationType} Save Failed`, {
        position: 'bottom-center',
      });
    }
  };

  const toggleShowAll = () => {
    let newShowAllState: showAllTypes = 'showAll';
    if (showAll === 'showAll') {
      newShowAllState = 'collapseAll';
    } else if (showAll === 'collapseAll') {
      newShowAllState = 'showAll';
    } else {
      newShowAllState = 'showAll';
    }
    setShowAll(newShowAllState);
  };

  return (
    <div className='flex flex-col gap-10 h-full relative'>
      { !editable && (
        <div className={'bg-alert-warning/20 p-2'}>
          <div className='font-medium'>
            { t(AE.notEditable) }{ ' ' }
            <span className='text-gray-700'>
              { t(AE.notEditableReason) }
            </span>
          </div>
        </div>
      ) }
      <div className='flex justify-between items-center '>
        <div className='flex items-center '>
          <button
              onClick={() => setShowAll('showAll')}
              className={`px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                showAll === 'showAll'
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
          >
            { t(AE.showAll) }
          </button>
          <button
              onClick={() => setShowAll('collapseAll')}
              className={`px-3 py-1.5 text-sm font-medium rounded-r-md border ${
                showAll === 'collapseAll'
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
          >
            { t(AE.collapseAll) }
          </button>
        </div>
        { evaluationType === 'Auto Outstanding' && (
          <div className=''>
            <Button
                onClick={
                  (importAutoSuitability && importAutoSuitability) || (() => {})
                }
                variant='primary'
                label={t(AE.importFromSuitability)}
                disabled={!editable}
            />
          </div>
        ) }
      </div>

      <div>
        { ' ' }
        <Collapsible
            title={t(AE.jobDescriptionDetails)}
            isOpen={showAll === 'showAll' || showAll === 'random'} // Always open in random state
            toggleOpen={() => {
              if (showAll === 'random') {
                setShowAll('collapseAll');
              } else {
                toggleShowAll();
              }
            }}
        >
          <div className='grid grid-cols-3 gap-5'>
            <div className=''>
              <div className='text-gray-800 py-1 text-sm'>{ t(AE.jobLocation) }</div>
              <TextInput
                  name='Job Location'
                  value={autoEvaluation.jobLocation}
                  type='text'
                  onChange={(event) => {
                    setAutoEvaluation({
                      ...autoEvaluation,
                      jobLocation: event.target.value,
                    });
                  }}
                  disabled={!editable}
              />
            </div>
            <RangeSelectInput
                disabled={!editable}
                label={t(AE.age)}
                selectOptions={rangeSelectOptions}
                onSelectChange={(selected: any) =>
                  setAutoEvaluation({
                    ...autoEvaluation,
                    age: {
                      ...autoEvaluation.age,
                      operator: selected.value,
                    },
                  })
                }
                selectValue={
                  rangeSelectOptions.find(
                    (option) => option.value === autoEvaluation.age.operator,
                  ) || rangeSelectOptions[0]
                }
                onTextChange={(event) => {
                  setAutoEvaluation({
                    ...autoEvaluation,
                    age: {
                      ...autoEvaluation.age,
                      value: parseInt(event.target.value),
                    },
                  });
                }}
                textValue={autoEvaluation.age.value.toString()}
                placeholder='Years'
                min='18'
                max='65'
            />
            <RangeSelectInput
                disabled={!editable}
                label={t(AE.workExperience)}
                selectOptions={rangeSelectOptions}
                onSelectChange={(selected: any) =>
                  setAutoEvaluation({
                    ...autoEvaluation,
                    workExperience: {
                      ...autoEvaluation.workExperience,
                      operator: selected.value,
                    },
                  })
                }
                selectValue={
                  rangeSelectOptions.find(
                    (option) =>
                      option.value === autoEvaluation.workExperience.operator,
                  ) || rangeSelectOptions[0]
                }
                onTextChange={(event) => {
                  setAutoEvaluation({
                    ...autoEvaluation,
                    workExperience: {
                      ...autoEvaluation.workExperience,
                      value: parseInt(event.target.value),
                    },
                  });
                }}
                textValue={autoEvaluation.workExperience.value.toString()}
                placeholder='Years'
                min='0'
                max='50'
            />
          </div>
        </Collapsible>{ ' ' }
      </div>

      <div className=''>
        <ListFields
            showAll={showAll}
            setShowAll={setShowAll}
            autoEvaluation={autoEvaluation}
            setAutoEvaluation={setAutoEvaluation}
            disabled={!editable}
            jobDescription={jobDescription}
            evaluationType={evaluationType}
        />
      </div>
      <div className='flex justify-end gap-3 pt-4 border-t'>
        <Button
            onClick={saveAutoEvaluation}
            variant='primary'
            label={`Save ${evaluationType}`}
            disabled={
              autoEvaluation.jobLocation === '' ||
            autoEvaluation.age.value === 0 ||
            autoEvaluation.educations === undefined ||
            autoEvaluation.certifications === undefined ||
            autoEvaluation.school === undefined ||
            autoEvaluation.skills === undefined ||
            autoEvaluation.keywords === undefined ||
            !editable
            }
        />
      </div>
    </div>
  );
};
